/* Soil Science result views — 10 analyses. Dark-brown theme #78350F. */
import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ScatterChart, Scatter, LineChart, Line,
} from 'recharts';

const BROWN = '#78350F';
const AMBER = '#92400E';
const PALETTE = ['#78350F', '#92400E', '#B45309', '#D97706', '#F59E0B', '#FBBF24', '#FDE68A', '#78716C', '#44403C', '#1C1917'];

const fmt = (v, n = 3) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') return v.toFixed(n);
  return String(v);
};

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #FDE68A' }} data-testid={testid}>
      <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: BROWN }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, cols, hiColFn }) {
  if (!rows || !rows.length) return <p className="text-xs text-gray-500">No data.</p>;
  const keys = cols || Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr style={{ background: '#FEF3C7' }}>
          {keys.map(k => <th key={k} className="px-2 py-1.5 border border-amber-200 text-left" style={{ color: BROWN }}>{String(k).replace(/_/g, ' ')}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#FFFBEB' }}>
              {keys.map(k => {
                const v = r[k];
                let cls = 'px-2 py-1 border border-amber-200 font-mono';
                if (hiColFn) cls += ' ' + (hiColFn(k, v) || '');
                const s = typeof v === 'number' ? fmt(v) : String(v ?? '-');
                return <td key={k} className={cls}>{s}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnovaTable({ anova }) {
  if (!anova?.anova) return null;
  return <Table rows={anova.anova} cols={['Source', 'DF', 'SS', 'MS', 'F', 'p_value', 'Sig']} />;
}

function StatCard({ label, value, unit }) {
  return (
    <div className="rounded-lg p-3 bg-white" style={{ border: '1px solid #FDE68A' }}>
      <div className="text-[10px] uppercase text-gray-500">{label}</div>
      <div className="font-mono text-base font-bold" style={{ color: BROWN }}>{value}{unit && <span className="text-[10px] text-gray-500 ml-1">{unit}</span>}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const colors = {
    Low: { bg: '#FEE2E2', fg: '#991B1B' },
    Deficient: { bg: '#FEE2E2', fg: '#991B1B' },
    'Strongly Acidic': { bg: '#FEE2E2', fg: '#991B1B' },
    'Strongly Alkaline': { bg: '#FEE2E2', fg: '#991B1B' },
    Medium: { bg: '#FEF3C7', fg: '#92400E' },
    Acidic: { bg: '#FEF3C7', fg: '#92400E' },
    Alkaline: { bg: '#FEF3C7', fg: '#92400E' },
    Optimal: { bg: '#D1FAE5', fg: '#065F46' },
    Sufficient: { bg: '#D1FAE5', fg: '#065F46' },
    High: { bg: '#DBEAFE', fg: '#1E40AF' },
    Healthy: { bg: '#D1FAE5', fg: '#065F46' },
    Moderate: { bg: '#FEF3C7', fg: '#92400E' },
    Degraded: { bg: '#FEE2E2', fg: '#991B1B' },
  };
  const c = colors[status] || { bg: '#E5E7EB', fg: '#374151' };
  return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: c.bg, color: c.fg }}>{status}</span>;
}

function Interp({ text }) {
  return (
    <p className="text-sm rounded-lg p-3" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#44403C' }} data-testid="interpretation">{text}</p>
  );
}

/* ==== 1. PHYSICAL ==== */
export function SoilPhysicalView({ data }) {
  if (!data) return null;
  const params = data.parameters_detected || [];
  return (
    <div className="space-y-4" data-testid="soil-physical-view">
      <Interp text={data.interpretation} />
      <Card title="Physical Properties Summary (incl. USDA Texture)" testid="phys-summary"><Table rows={data.physical_summary_table} /></Card>
      {data.texture_classification?.length > 0 && (
        <Card title="USDA Texture Classification" testid="texture-table"><Table rows={data.texture_classification.slice(0, 30)} /></Card>
      )}
      {data.depth_trend_regressions?.length > 0 && (
        <Card title="Depth-Wise Trend (regression)" testid="depth-trend"><Table rows={data.depth_trend_regressions} /></Card>
      )}
      <Card title="Correlation Matrix" testid="phys-corr"><Table rows={data.correlation_matrix} /></Card>
      <Card title="ANOVA per parameter" testid="phys-anovas">
        {params.slice(0, 6).map(p => (
          <div key={p} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: BROWN }}>{p.replace(/_/g, ' ')} — p = {fmt(data.anova_per_parameter?.[p]?.p_value, 6)}</div>
            <AnovaTable anova={data.anova_per_parameter?.[p]} />
          </div>
        ))}
      </Card>
    </div>
  );
}

/* ==== 2. CHEMICAL ==== */
export function SoilChemicalView({ data }) {
  if (!data) return null;
  const statuses = ['Deficient', 'Low', 'Medium', 'Acidic', 'Alkaline', 'Optimal', 'High', 'Sufficient', 'Strongly Acidic', 'Strongly Alkaline'];
  return (
    <div className="space-y-4" data-testid="soil-chemical-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Parameters" value={(data.parameters_detected || []).length} />
        <StatCard label="Deficiency alerts" value={(data.deficiency_alerts || []).length} />
        <StatCard label="Top fertility" value={data.fertility_index_ranking?.[0]?.Location || '-'} />
        <StatCard label="Plots analysed" value={data.nutrient_status_table?.length || 0} />
      </div>

      <Card title="Chemical Properties (means with Δ-pH, C:N)" testid="chem-summary"><Table rows={data.chemical_summary_table} /></Card>

      <Card title="ICAR Nutrient Status Classification" testid="nut-status">
        <Table rows={data.nutrient_status_table}
          hiColFn={(k, v) => (statuses.includes(v) ? '' : '')} />
        <div className="mt-2 flex flex-wrap gap-1">
          {Array.from(new Set((data.nutrient_status_table || []).flatMap(r => Object.values(r)).filter(v => typeof v === 'string' && v !== '-'))).map(s => (
            <StatusChip key={s} status={s} />
          ))}
        </div>
      </Card>

      {data.deficiency_alerts?.length > 0 && (
        <Card title="Deficiency Alerts &amp; Recommendations" testid="deficiency-alerts">
          <Table rows={data.deficiency_alerts} />
        </Card>
      )}

      <Card title="Fertility Index Ranking (composite 0-100)" testid="fi-ranking"><Table rows={data.fertility_index_ranking} /></Card>

      <Card title="Correlation Matrix" testid="chem-corr"><Table rows={data.correlation_matrix} /></Card>
    </div>
  );
}

/* ==== 3. STCR ==== */
export function SoilSTCRView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-stcr-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Plots" value={data.n_plots} />
        <StatCard label="R²" value={fmt(data.r_squared, 4)} />
        <StatCard label="Adj. R²" value={fmt(data.adj_r_squared, 4)} />
        <StatCard label="Equation fitted" value="Linear multi-predictor" />
      </div>
      <Card title="STCR Regression Equation" testid="stcr-equation">
        <code className="block text-xs bg-amber-50 rounded p-2 font-mono break-words" style={{ color: BROWN }}>{data.equation}</code>
      </Card>
      <Card title="Regression Coefficients" testid="stcr-coef"><Table rows={data.regression_coefficients} /></Card>
      {data.target_yield_recommendations?.length > 0 && (
        <Card title="Fertilizer Recommendation for Target Yield (N held variable, P &amp; K at mean)" testid="target-yield"><Table rows={data.target_yield_recommendations} /></Card>
      )}
      {data.nue_table?.length > 0 && (
        <Card title="Partial Factor Productivity (PFP) by N dose" testid="nue-table"><Table rows={data.nue_table} /></Card>
      )}
    </div>
  );
}

/* ==== 4. SOIL HEALTH INDEX ==== */
export function SoilHealthIndexView({ data }) {
  if (!data) return null;
  const indicators = data.indicators_detected || [];
  const topBottom = [...(data.shi_ranking || []).slice(0, 3), ...(data.shi_ranking || []).slice(-3)];
  return (
    <div className="space-y-4" data-testid="soil-shi-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Treatments" value={data.shi_ranking?.length || 0} />
        <StatCard label="Best SHI" value={data.best_shi || '-'} />
        <StatCard label="Healthy" value={data.classification_summary?.Healthy || 0} />
        <StatCard label="Degraded" value={data.classification_summary?.Degraded || 0} />
      </div>
      <Card title="SHI Ranking" testid="shi-ranking">
        <Table rows={(data.shi_ranking || []).map(r => ({
          Treatment: r.Treatment, SHI: r.SHI, Classification: r.Classification, Rank: r.Rank,
        }))} />
      </Card>
      {data.improvement_priority?.length > 0 && (
        <Card title="Improvement Priority (lowest-scoring indicators)" testid="improvement-priority">
          <Table rows={data.improvement_priority} />
        </Card>
      )}
      {data.minimum_data_set && (
        <Card title="PCA-based Minimum Data Set (MDS)" testid="mds-card">
          <div className="text-xs text-gray-700 mb-2">
            Retained {data.minimum_data_set.n_pcs_selected} PCs explaining {data.minimum_data_set.total_variance_explained}% of variance.
          </div>
          <Table rows={data.minimum_data_set.mds_indicators?.map(ind => ({ Indicator: ind })) || []} />
        </Card>
      )}
      {topBottom.length > 0 && indicators.length > 0 && (
        <Card title="Top 3 vs Bottom 3 — Indicator Scores Radar" testid="shi-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={data.radar_top_bottom}>
              <PolarGrid />
              <PolarAngleAxis dataKey="Indicator" fontSize={10} />
              <PolarRadiusAxis angle={30} domain={[0, 1]} tick={false} />
              {topBottom.map((r, i) => <Radar key={r.Treatment} name={r.Treatment} dataKey={r.Treatment} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.18} />)}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

/* ==== 5. NUTRIENT AVAILABILITY ==== */
export function SoilNutrientAvailView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-nutrient-view">
      <Interp text={data.interpretation} />
      <Card title="Nutrient Budget (inputs - outputs)" testid="budget-table"><Table rows={data.nutrient_budget_table} /></Card>
      <Card title="NUE Indices (NUE%, PFP, Balance)" testid="nue-table">
        <Table rows={data.nue_table} />
      </Card>
      {(data.nue_table || []).filter(r => r.Nutrient === 'N').length > 0 && (
        <Card title="N Balance per Treatment" testid="n-balance-bar">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(data.nue_table || []).filter(r => r.Nutrient === 'N')}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Treatment" angle={-25} textAnchor="end" height={80} fontSize={10} />
              <YAxis label={{ value: 'N balance (kg/ha)', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="Balance_kg_ha" fill={AMBER} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

/* ==== 6. MICROBIAL ==== */
export function SoilMicrobialView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-microbial-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Parameters" value={(data.parameters_detected || []).length} />
        <StatCard label="Best EAI" value={data.best_treatment_by_EAI || '-'} />
        <StatCard label="Shannon mean" value={fmt((data.diversity_indices || []).reduce((s, r) => s + (r.Shannon_H || 0), 0) / Math.max((data.diversity_indices || []).length, 1), 2)} />
      </div>
      <Card title="Microbial Parameters" testid="microbial-summary"><Table rows={data.microbial_summary_table} /></Card>
      {data.diversity_indices?.length > 0 && (
        <Card title="Diversity Indices (Shannon H′, Simpson D, Evenness)" testid="diversity-table"><Table rows={data.diversity_indices} /></Card>
      )}
      {data.enzyme_activity_index?.length > 0 && (
        <Card title="Enzyme Activity Index Ranking" testid="eai-table"><Table rows={data.enzyme_activity_index} /></Card>
      )}
    </div>
  );
}

/* ==== 7. SOIL CARBON ==== */
export function SoilCarbonView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-carbon-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Best SOC" value={data.best_treatment_by_SOC || '-'} />
        <StatCard label="Top CMI" value={data.cmi_ranking?.[0]?.Treatment || '-'} unit={data.cmi_ranking?.[0]?.CMI ? `(${data.cmi_ranking[0].CMI})` : ''} />
        <StatCard label="Sequestration observed" value={(data.sequestration_rate_table || []).length} />
        <StatCard label="SOC p" value={fmt(data.anova_soc?.p_value, 6)} />
      </div>
      <Card title="SOC Stock Table (Mg C/ha) + fractions + CMI" testid="soc-table"><Table rows={data.soc_stock_table} /></Card>
      {data.sequestration_rate_table?.length > 0 && (
        <Card title="Sequestration Rate (Mg C/ha/yr + CO₂-eq via IPCC Tier-1)" testid="seq-table">
          <Table rows={data.sequestration_rate_table} />
        </Card>
      )}
      {data.cmi_ranking?.length > 0 && (
        <Card title="Carbon Management Index (CMI) Ranking" testid="cmi-table">
          <Table rows={data.cmi_ranking} />
        </Card>
      )}
    </div>
  );
}

/* ==== 8. SOIL QUALITY INDEX ==== */
export function SoilQualityIndexView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-sqi-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Land uses" value={data.sqi_ranking?.length || 0} />
        <StatCard label="Best SQI" value={data.best_land_use || '-'} />
        <StatCard label="Indicators total" value={data.n_indicators_used} />
        <StatCard label="MDS size" value={data.n_indicators_mds} />
      </div>
      <Card title="SQI Ranking" testid="sqi-ranking"><Table rows={data.sqi_ranking} /></Card>
      <Card title="Minimum Data Set (PCA-based)" testid="sqi-mds"><Table rows={data.minimum_data_set} /></Card>
      <Card title="Cumulative Variance Explained" testid="sqi-variance">
        <Table rows={(data.variance_explained_cumulative || []).map((v, i) => ({ PC: `PC${i + 1}`, Cumulative_variance_percent: v }))} />
      </Card>
      {data.pca_pc_scores?.length > 0 && (
        <Card title="PCA Biplot (PC1 vs PC2)" testid="sqi-biplot">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="PC1" name="PC1" />
              <YAxis dataKey="PC2" name="PC2" />
              <Tooltip cursor={{ strokeDasharray: '3 3' }}
                content={({ payload }) => payload && payload.length ? (
                  <div className="bg-white p-2 border border-amber-200 text-xs">
                    {payload[0].payload.Land_use}: PC1={fmt(payload[0].payload.PC1, 2)}, PC2={fmt(payload[0].payload.PC2, 2)}
                  </div>) : null} />
              <Scatter data={data.pca_pc_scores} fill={AMBER} />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

/* ==== 9. FERTILIZER RESPONSE ==== */
export function SoilFertilizerResponseView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-fert-resp-view">
      <Interp text={data.interpretation} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="R²" value={fmt(data.r_squared, 4)} />
        <StatCard label="Agronomic optimum" value={data.agronomic_optimum_dose_kg_ha} unit="kg/ha" />
        <StatCard label="Economic optimum" value={data.economic_optimum_dose_kg_ha} unit="kg/ha" />
        <StatCard label="Max yield" value={data.max_yield_t_ha} unit="t/ha" />
      </div>
      <Card title="Quadratic Response Equation" testid="fert-eq">
        <code className="block text-xs bg-amber-50 rounded p-2 font-mono break-words" style={{ color: BROWN }}>{data.quadratic_equation}</code>
      </Card>
      <Card title="Dose-Response Curve" testid="fert-curve">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.dose_response_curve}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Dose" label={{ value: 'Fertilizer dose (kg/ha)', position: 'insideBottom', offset: -2 }} />
            <YAxis label={{ value: 'Predicted yield (t/ha)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Line type="monotone" dataKey="Predicted_yield" stroke={AMBER} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
      {data.response_ratios?.length > 0 && (
        <Card title="Response Ratios per Treatment" testid="response-ratios">
          <Table rows={data.response_ratios} />
        </Card>
      )}
      <Card title="Price Assumptions Used for EOD" testid="price-assumptions">
        <Table rows={[data.price_assumptions]} />
      </Card>
    </div>
  );
}

/* ==== 10. LAND USE CHANGE ==== */
export function SoilLandUseChangeView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="soil-land-use-view">
      <Interp text={data.interpretation} />
      <Card title="Land-Use Summary Means" testid="lu-summary"><Table rows={data.land_use_summary_table} /></Card>
      <Card title={`% Change vs Reference (${data.reference_land_use})`} testid="lu-change"><Table rows={data.percent_change_vs_reference} /></Card>
      {data.temporal_trend?.length > 0 && (
        <Card title="Temporal Trend" testid="lu-trend"><Table rows={data.temporal_trend} /></Card>
      )}
      <Card title="ANOVA per indicator" testid="lu-anovas">
        {(data.indicators_analysed || []).slice(0, 6).map(ind => (
          <div key={ind} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: BROWN }}>{ind.replace(/_/g, ' ')} — p = {fmt(data.anova_per_parameter?.[ind]?.p_value, 6)}</div>
            <AnovaTable anova={data.anova_per_parameter?.[ind]} />
          </div>
        ))}
      </Card>
    </div>
  );
}
